import React from 'react';
import styled from 'styled-components';

import { MatcherStatus, MatchingTaskCounts } from './matchingAdmin.types';

const ActivityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0 16px;
  text-align: right;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  thead th {
    color: #64748b;
    font-size: 12px;
    font-weight: 500;
    padding: 0 12px 12px;
  }
  th:first-child {
    text-align: left;
    padding-left: 0;
  }
  tbody tr {
    border-top: 1px solid #dce4ed;
  }
  tbody th {
    width: 25%;
    padding: 22px 12px 22px 0;
    vertical-align: middle;
    font-size: 15px;
    font-weight: 600;
    color: #183955;
  }
  tbody td {
    padding: 22px 12px;
    font-size: 23px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .matching-stage-description {
    display: block;
    color: #64748b;
    font-weight: 400;
    font-size: 12px;
    margin-top: 6px;
  }
  .matching-count-success {
    color: #183955;
  }
  .matching-count-muted {
    color: #94a3b8;
    font-weight: 400;
  }
  .matching-count-failed {
    color: #b42318;
  }
  .matching-count-retrying {
    color: #966119;
  }
  .matching-mobile-count-label {
    display: none;
  }
  @media (max-width: 991px) {
    display: block;
    thead {
      display: none;
    }
    tbody {
      display: block;
    }
    tbody tr {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      padding: 20px 0;
      gap: 16px;
      text-align: left;
    }
    tbody th {
      grid-column: 1 / -1;
      width: auto;
      padding: 0 0 4px;
    }
    tbody td {
      padding: 0;
    }
    .matching-mobile-count-label {
      display: block;
      color: #64748b;
      font-size: 12px;
      font-weight: 400;
      margin-bottom: 6px;
    }
  }
`;

const columns: { key: keyof MatchingTaskCounts; label: string }[] = [
  { key: 'successful', label: 'Successful' },
  { key: 'queued', label: 'Queued' },
  { key: 'running', label: 'Running' },
  { key: 'retrying', label: 'Retrying' },
  { key: 'failed', label: 'Failed' },
  { key: 'blocked', label: 'Blocked' },
];

const stageLabels = [
  { key: 'quick', name: 'Quick checks', description: 'Initial fit screening' },
  { key: 'detailed', name: 'Detailed assessments', description: 'Full assessment after a quick pass' },
] as const;

export const MatcherStageActivity = ({ stages }: Pick<MatcherStatus, 'stages'>) => (
  <ActivityTable>
    <caption>Matching tasks by stage</caption>
    <thead>
      <tr>
        <th scope="col">Stage</th>
        {columns.map(({ key, label }) => (
          <th key={key} scope="col">
            {label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {stageLabels.map(({ key, name, description }) => (
        <tr key={key}>
          <th scope="row">
            {name}
            <span className="matching-stage-description">{description}</span>
          </th>
          {columns.map(({ key: count, label }) => {
            const value = stages[key][count];
            const tone = value === 0 ? 'muted' : count === 'successful' ? 'success' : count;
            return (
              <td key={count} className={`matching-count-${tone}`}>
                <span className="matching-mobile-count-label">{label}</span>
                {value.toLocaleString()}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  </ActivityTable>
);
